import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';
import { translations, stepsTranslations } from '../translations/content';

const {
  FiTarget, FiTrendingUp, FiLightbulb, FiUsers, FiCompass, FiDollarSign,
  FiPackage, FiMegaphone, FiRocket, FiZap, FiArrowRight, FiMail
} = FiIcons;

const stepIcons = [
  FiTarget, FiTrendingUp, FiLightbulb, FiUsers, FiCompass,
  FiDollarSign, FiPackage, FiMegaphone, FiRocket, FiZap
];

const stepColors = [
  { from: 'from-blue-500', to: 'to-cyan-400', bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  { from: 'from-purple-500', to: 'to-pink-400', bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
  { from: 'from-yellow-500', to: 'to-orange-400', bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' },
  { from: 'from-green-500', to: 'to-emerald-400', bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  { from: 'from-indigo-500', to: 'to-blue-400', bg: 'bg-indigo-50', text: 'text-indigo-600', border: 'border-indigo-200' },
  { from: 'from-red-500', to: 'to-pink-400', bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' },
  { from: 'from-teal-500', to: 'to-cyan-400', bg: 'bg-teal-50', text: 'text-teal-600', border: 'border-teal-200' },
  { from: 'from-orange-500', to: 'to-amber-400', bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200' },
  { from: 'from-violet-500', to: 'to-purple-400', bg: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  { from: 'from-rose-500', to: 'to-pink-400', bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' }
];

const StepsOverview = ({ steps: propsSteps }) => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const t = translations[lang].overview;
  const p = translations[lang].paths;
  
  const steps = propsSteps || stepsTranslations[lang];

  return (
    <div className="min-h-screen pt-24 pb-0 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-100 -z-10" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-20">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-block mb-6">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-black uppercase tracking-widest shadow-lg">
              <SafeIcon icon={FiTarget} className="mr-2" />
              {t.step} 1-10
            </span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 tracking-tight leading-tight">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto font-medium leading-relaxed">
            {t.subtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps && steps.map((step, index) => {
            const colors = stepColors[index] || stepColors[0];
            const Icon = stepIcons[index] || FiTarget;
            
            return (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="group relative"
              >
                <Link to={`${p.steps}/${step.id}`} className="block">
                  <div className={`relative bg-white rounded-[2rem] p-8 shadow-sm border-2 ${colors.border} hover:shadow-2xl transition-all duration-300 overflow-hidden h-full`}>
                    <div className="relative z-10">
                      <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl ${colors.bg} ${colors.text} mb-6`}>
                        <SafeIcon icon={Icon} className="text-3xl" />
                      </div>
                      <h3 className="text-2xl font-black text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 font-medium leading-relaxed mb-6">{step.subtitle}</p>
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <span className="text-sm font-bold text-gray-400 group-hover:text-gray-600 transition-colors uppercase tracking-wider">{t.learnMore}</span>
                        <div className={`w-8 h-8 rounded-full ${colors.bg} ${colors.text} flex items-center justify-center`}>
                          <SafeIcon icon={FiArrowRight} className="text-sm" />
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </div>

      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gray-900 rounded-[2.5rem] p-10 md:p-16 text-center text-white shadow-2xl relative overflow-hidden"
          >
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-black mb-6 tracking-tight">
                {lang === 'de' ? 'Bereit für deinen unternehmerischen Erfolg?' : 'Ready for your entrepreneurial success?'}
              </h2>
              <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
                {lang === 'de'
                  ? 'Erhalte sofortigen Zugriff auf alle detaillierten Aktionspläne und Vorlagen durch eine kostenlose Registrierung.'
                  : 'Get instant access to all detailed action plans and templates through a free registration.'
                }
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <button
                  onClick={() => navigate(p.register)}
                  className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-blue-600 text-white px-10 py-4 rounded-xl font-black text-lg hover:bg-blue-700 transition-all transform hover:-translate-y-1 shadow-lg shadow-blue-900/20"
                >
                  <span>{lang === 'de' ? 'Kostenlos registrieren' : 'Register for Free'}</span>
                  <SafeIcon icon={FiZap} />
                </button>
                
                <Link
                  to={lang === 'de' ? '/kontakt' : '/contact'}
                  className="w-full sm:w-auto inline-flex items-center justify-center space-x-2 bg-white text-gray-900 px-10 py-4 rounded-xl font-black text-lg hover:bg-gray-100 transition-all transform hover:-translate-y-1 shadow-lg"
                >
                  <span>{lang === 'de' ? 'Fragen stellen' : 'Have Questions?'}</span>
                  <SafeIcon icon={FiMail} className="text-blue-600" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default StepsOverview;